import { useState } from "react";

import { useWorkspace } from "@/components/helpers/custom-hooks/use-workspace";
import {
  QueryPaginatedRoleplaysOrderBy,
  usePaginatedRoleplaysQuery,
  VisibilityScope,
} from "@/graphql";

const FETCH_LIMIT = 10;

export const useRoleplayListQuery = ({
  includeOwnerData,
  skip = false,
  pageSize = FETCH_LIMIT,
  workspaceOnly = false,
}: {
  includeOwnerData?: boolean;
  skip?: boolean;
  pageSize?: number;
  workspaceOnly?: boolean;
}) => {
  const workspace = useWorkspace();
  const [searchQuery, setSearchQuery] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedOwner, setSelectedOwner] = useState<string | null>(null);
  const [selectedScopes, setSelectedScopes] = useState<VisibilityScope[]>(
    [
      VisibilityScope.User,
      workspace ? VisibilityScope.Workspace : null,
      VisibilityScope.Global,
    ].filter(Boolean) as VisibilityScope[],
  );
  const [error, setError] = useState<string | null>(null);
  const [order, setOrder] = useState<QueryPaginatedRoleplaysOrderBy>(
    QueryPaginatedRoleplaysOrderBy.Default,
  );
  const [selectedTags, setSelectedTags] = useState<string[]>([]);

  const {
    data,
    loading: loadingData,
    refetch: refetchRoleplays,
  } = usePaginatedRoleplaysQuery({
    fetchPolicy: "cache-and-network",
    variables: {
      currentPage: currentPage,
      pageSize: pageSize,
      query: searchQuery,
      scopes: selectedScopes,
      ownerIds: selectedOwner ? [selectedOwner] : null,
      workspaceOnly: workspaceOnly,
      tagIds: selectedTags,
      includeOwnerData: includeOwnerData,
      orderBy: order,
    },
    skip,
    onError: () => {
      setError("Error fetching roleplays");
    },
  });

  const totalRecords = data?.paginatedRoleplays?.totalRecords || 0;
  return {
    roleplays: data?.paginatedRoleplays?.data || [],
    totalRecords,
    loading: loadingData,
    error,
    refetchRoleplays,
    setSelectedOwner,
    setSelectedTags,
    setOrder,
    setSelectedScopes,
    setCurrentPage,
    setSearchQuery,
    selectedScopes,
    selectedOwner,
    selectedTags,
    order,
    currentPage,
    searchQuery,
  };
};
